/**
 * mapLineFields — shared line-level camelCase → snake_case field mapper.
 *
 * Governed by PT-53A W1.4b · response §8 architecture:
 *
 *   "Extract the line-level camelCase→snake_case mapper as a shared primitive.
 *    Two thin serializers (`serializeQuoteLine`, `serializeChangeOrderLine`)
 *    both call it. Each owns only its collection key and envelope-specific
 *    fields."
 *
 * This is the primitive that keeps getting got wrong. Prior to this
 * extraction the mapping lived inline inside `quotePrefill.serializeLineItem`,
 * and the change-order write path bypassed it entirely — sending raw
 * camelCase builder state as `{lines: coLines, ...}` (DEF-086 / DEF-096).
 *
 * Field mapping (source: PT-53 §53.3a in quotePrefill.js):
 *
 *   productLabel | item_name | description → item_name
 *   categoryId   | category                → category
 *   materialCost + laborCost + otherCost   → cost_basis (numeric sum)
 *   lineMargin (finite, ≥0)                → margin_pct + margin_override
 *                                             (PT-31 1→2 field split)
 *   quantity | sqft                        → (derived unit_rate divisor)
 *   line_total | lineTotal | (computed)    → line_total
 *   line_total / quantity, rounded to cents → unit_rate
 *
 * Consumers:
 *   • serializeQuoteLine (in quotePrefill.js) — passes an optional
 *     `computed` (engine-computed total) that takes precedence over
 *     line.lineTotal.
 *   • serializeChangeOrderLine (in change-order/changeOrderSerializer.js) —
 *     does NOT pass `computed`; CO working lines already carry the
 *     computed `lineTotal` in state.
 *
 * Byte-identical output contract with the pre-extraction inline mapper.
 * Verified by the existing quotePrefill.test.js PT-53 §53.3 suite (the
 * quote regression fence for W1.4b).
 */

/**
 * PT-31 margin split: `lineMargin` on the builder is a per-line override.
 * On the wire this must round-trip as TWO fields so the backend can
 * distinguish "no override" (override=false, pct=0) from "explicit 0%"
 * (override=true, pct=0). PT-30's zero-is-valid rule requires this.
 */
export function serializeLineMargin(line) {
  const v = line?.lineMargin;
  const isSet = v != null && Number.isFinite(v) && v >= 0;
  return {
    margin_pct: isSet ? v : undefined,
    margin_override: isSet,
  };
}

/**
 * Resolve the per-line total in cents. Resolution order:
 *   1. Engine-computed total (when caller supplies it).
 *   2. Builder-native `lineTotal` (manual price override).
 *   3. Backend-round-tripped `line_total`.
 *   4. 0.
 */
function resolveLineTotal(line, computed) {
  const computedTotal = computed && Number.isFinite(computed.total) ? computed.total : null;
  if (computedTotal !== null) return computedTotal;
  if (Number.isFinite(line.lineTotal)) return line.lineTotal;
  if (Number.isFinite(line.line_total)) return line.line_total;
  return 0;
}

/**
 * Map a builder-native (camelCase) line to the snake_case display contract.
 *
 * @param {object}  line       Builder-native line-item state.
 * @param {object=} computed   Optional engine-computed line { total }.
 *                             Quote path supplies this from `useQuoteMargins`;
 *                             CO path doesn't (CO working lines already
 *                             carry their `lineTotal` in state).
 * @returns {object}           `{ item_name, category, cost_basis, unit_rate,
 *                              line_total, margin_pct, margin_override }` —
 *                             the six-field display contract. Callers spread
 *                             the source line first to preserve builder-native
 *                             keys for rehydrate.
 */
export function mapLineFields(line, computed = null, opts = {}) {
  const { margin_pct, margin_override } = serializeLineMargin(line);
  const materialCost = Number(line.materialCost) || 0;
  const laborCost = Number(line.laborCost) || 0;
  const otherCost = Number(line.otherCost) || 0;
  // B0/B5: cost_basis is "the cost the price was derived from" — it MUST include
  // additionalLabor and additionalCost (in dollars, converted to cents) so that
  // cost_basis / (1 - margin) == line_total. Without this, the server's
  // DeriveAcceptedTotal produces a different number and signing returns 409.
  const additionalLaborCents = Math.round(
    (Number(line.additionalLabor ?? line.additional_labor ?? 0) || 0) * 100
  );
  const additionalCostCents = Math.round(
    (Number(line.additionalCost ?? line.additional_cost ?? 0) || 0) * 100
  );
  const totalCost = materialCost + laborCost + otherCost + additionalLaborCents + additionalCostCents;
  const lineTotal = resolveLineTotal(line, computed);
  const quantity = Number(line.quantity) || Number(line.sqft) || 0;
  const unitRate = quantity > 0 ? Math.round((lineTotal / quantity) * 100) / 100 : 0;
  // §2 (RS-2, 2026-08-07): stamp intent ONLY at the send boundary.
  //   Writing the stamp on every autosave makes it self-perpetuating: the
  //   next read reads its own write, and a draft stops responding to settings
  //   changes. Per DOCUMENT_SURFACE_SPEC RS-2, stamps write at SEND.
  //   Caller must opt in via `{ stampIntent: true }`.
  const stampIntent = opts && opts.stampIntent === true;
  const stampFields = stampIntent && computed
    ? {
        resolved_margin: Number.isFinite(computed.margin) ? computed.margin : null,
        margin_source: typeof computed.marginSource === 'string' ? computed.marginSource : null,
        labor_source: typeof line.laborSource === 'string' ? line.laborSource : null,
      }
    : {};
  return {
    item_name: line.productLabel || line.item_name || line.description || '',
    category: line.categoryId || line.category || 'other',
    cost_basis: totalCost,
    unit_rate: unitRate,
    line_total: lineTotal,
    margin_pct,
    margin_override,
    ...stampFields,
  };
}
